export type CatalogModel = {
  id: string;
  name: string;
  thumbnail: string;
  colorCount: number;
  priceFrom: number;
  categoryId: string;
};

export type CatalogModelDetail = CatalogModel & {
  colors?: Array<{
    key: string;
    name?: string;
    hex?: string;
    sizes?: Array<string | { id?: string; label?: string; order?: number }>;
    productId?: number;
    variations?: Array<{
      id: number;
      size?: string;
      attributes?: Record<string, string>;
    }>;
  }>;
  sizes?: Array<string | { id?: string; label?: string; order?: number }>;
  /** From ox/v1/models/{id} — preferred source for catalogRef */
  variants?: Array<{
    id: string;
    colorKey?: string;
    sizeId?: string;
    price?: { amount: number; currency: string };
  }>;
  wooProductId?: number;
  /** Print-only service models (garment type / bring-your-own shirt). */
  printService?: boolean;
  askGarmentColor?: boolean;
  askGarmentPrintType?: boolean;
};

export type QuoteLineIn = { variantId: string; quantity: number };
export type QuoteResult = {
  lines: Array<{
    variantId: string;
    quantity: number;
    unitPrice: { amount: number; currency: string };
    lineTotal: { amount: number; currency: string };
    label?: string;
  }>;
  subtotal: { amount: number; currency: string };
  total: { amount: number; currency: string };
  extras?: { amount: number; currency: string };
  extrasUnit?: { amount: number; currency: string };
  breakdown?: Array<{ code: string; label: string; amount: number; included?: boolean }>;
  viewMetrics?: Array<Record<string, unknown>>;
};

export type StudioTemplate = {
  id: string;
  title: string;
  description: string;
  status: string;
  catalogProductRef: string;
  designRef: string | null;
  designVersionRef: string | null;
  wizardStep: string;
  previewUrl: string | null;
  variants: Array<{
    id: string;
    size: string;
    color: string;
    catalogRef: string;
    costAmount: number;
    retailAmount: number;
    currency: string;
    active: boolean;
    profitAmount?: number;
  }>;
  collections: Array<{ id: string; name?: string; slug?: string }>;
  createdAt: string;
  updatedAt: string;
};

export type StudioCollection = { id: string; name: string; slug: string };

export type StudioChannel = {
  id: string;
  kind: string;
  name: string;
  status: string;
};

export interface StudioPorts {
  identity: {
    getToken: () => Promise<string | null>;
  };
  catalog: {
    listModels: (filter?: { categoryId?: string }) => Promise<CatalogModel[]>;
    getModel: (id: string) => Promise<CatalogModelDetail | null>;
  };
  pricing: {
    quote: (input: {
      lines: QuoteLineIn[];
      designId?: string;
      /** Catalog regular price (ignore membership). Used for template buy/order. */
      forceRegularPrice?: boolean;
    }) => Promise<QuoteResult>;
  };
  cart: {
    addToCart: (input: {
      designId: string;
      versionId?: string;
      lines: QuoteLineIn[];
      viewMetrics?: Array<Record<string, unknown>>;
      /** Catalog regular + print extras (no member discount). */
      forceRegularPrice?: boolean;
    }) => Promise<{ cartCount?: number; redirectUrl?: string }>;
  };
  studio: {
    listTemplates: (opts?: {
      limit?: number;
      status?: string;
      collectionId?: string;
    }) => Promise<{ items: StudioTemplate[]; nextCursor?: string | null }>;
    getTemplate: (id: string) => Promise<StudioTemplate>;
    createTemplate: (input: {
      catalogProductRef: string;
      title?: string;
    }) => Promise<StudioTemplate>;
    /** Gelato-style: design + product → ready template (default 50% margin). */
    createFromDesign?: (input: {
      title: string;
      catalogProductRef: string;
      designRef: string;
      designVersionRef?: string;
      description?: string;
      collectionIds?: string[];
      marginPercent?: number;
      status?: string;
      variants: Array<{
        size: string;
        color?: string;
        catalogRef?: string;
        costAmount?: number;
        retailAmount?: number;
        currency?: string;
        active?: boolean;
        sortOrder?: number;
      }>;
    }) => Promise<StudioTemplate>;
    updateTemplate: (
      id: string,
      patch: Partial<{
        title: string;
        description: string;
        status: string;
        designRef: string | null;
        designVersionRef: string | null;
        wizardStep: string;
        collectionIds: string[];
      }>,
    ) => Promise<StudioTemplate>;
    deleteTemplate: (id: string) => Promise<{ ok: boolean }>;
    putVariants: (
      id: string,
      variants: Array<{
        size: string;
        color?: string;
        catalogRef?: string;
        costAmount?: number;
        retailAmount?: number;
        currency?: string;
        active?: boolean;
        sortOrder?: number;
      }>,
    ) => Promise<StudioTemplate>;
    listCollections: () => Promise<{ items: StudioCollection[] }>;
    createCollection: (name: string) => Promise<StudioCollection>;
    listChannels: () => Promise<{ items: StudioChannel[] }>;
  };
}

export type StudioView = 'list' | 'wizard' | 'order' | 'connect-store';

export type WizardStepId = 'product' | 'design' | 'details' | 'prices' | 'review';

/** Declared in one place so mockups can be inserted later without touching every screen. */
export const WIZARD_STEPS: Array<{ id: WizardStepId; label: string }> = [
  { id: 'product', label: 'Producto' },
  { id: 'design', label: 'Diseñar' },
  { id: 'details', label: 'Detalles' },
  { id: 'prices', label: 'Precios' },
  { id: 'review', label: 'Revisar' },
];

export interface StudioAppProps {
  ports: StudioPorts;
  studioApiBase: string;
  builderApiBase?: string;
  designerScriptUrl?: string;
  /** Ox Designer theme (from Ox Designer settings) for the embedded editor. */
  designerAccent?: string;
  designerOnAccent?: string;
  designerHighlight?: string;
  initialTemplateId?: string;
  initialView?: StudioView;
  createUrl?: string;
  listUrl?: string;
  /** Storefront URL — escape hatch from fullscreen Studio. */
  shopUrl?: string;
  /** Fullscreen Ox Designer page (Personalizar). Used for bring-your-own print orders. */
  designerPageUrl?: string;
}
